import {
  applySingleValueOptions,
  fieldMatches,
  getCatalogOptionKeys,
  norm,
  parseOrderSizeColor,
} from './luxuryproFulfill';

export { parseOrderSizeColor, orderHasDesignForLuxuryPro as orderHasDesignForAnprint } from './luxuryproFulfill';

/**
 * Map giá trị order → đúng 1 value đã có trong option Anprint.
 * Ưu tiên: exact → exact sau khi bỏ khoảng trắng → includes (chọn value ngắn nhất khớp).
 */
export const matchAnprintOptionValue = (wanted, values = []) => {
  const list = (values || []).filter(Boolean);
  if (!list.length || wanted == null || String(wanted).trim() === '') return null;

  const w = norm(wanted);
  const exact = list.find((v) => norm(v) === w);
  if (exact) return exact;

  const candidates = list.filter((v) => fieldMatches(v, wanted));
  if (!candidates.length) return null;
  if (candidates.length === 1) return candidates[0];

  // Prefer exact-ish: shortest Anprint label that still matches (tránh "Black" → "Camo Black" khi có "Black")
  candidates.sort((a, b) => {
    const na = norm(a);
    const nb = norm(b);
    const score = (n) => (n === w ? 0 : n.includes(w) || w.includes(n) ? 1 : 2);
    const s = score(na) - score(nb);
    if (s !== 0) return s;
    return na.length - nb.length;
  });
  return candidates[0];
};

export const buildAnprintPicksFromOrder = (catalog, orderSize, orderColor) => {
  const picks = {};
  for (const opt of catalog?.options || []) {
    if (opt.option === 'size' && orderSize) {
      const matched = matchAnprintOptionValue(orderSize, opt.values);
      if (matched) picks.size = matched;
    }
    if (opt.option === 'color' && orderColor) {
      const matched = matchAnprintOptionValue(orderColor, opt.values);
      if (matched) picks.color = matched;
    }
  }
  return picks;
};

export const findAnprintVariant = (catalog, picks) => {
  const keys = getCatalogOptionKeys(catalog);
  if (!keys.every((k) => picks[k])) return null;
  // Exact match trên giá trị Anprint đã snap (không fuzzy lại)
  return (
    (catalog.variants || []).find((v) =>
      keys.every((k) => norm(v[k]) === norm(picks[k]))
    ) || null
  );
};

export const resolveAnprint = (catalog, orderItem, manualPicks = {}) => {
  const { orderSize, orderColor } = parseOrderSizeColor(orderItem);
  const keys = getCatalogOptionKeys(catalog);

  let picks = buildAnprintPicksFromOrder(catalog, orderSize, orderColor);
  picks = applySingleValueOptions(catalog, picks);

  // Manual picks cũng phải nằm trong option Anprint
  const snappedManual = {};
  for (const [key, val] of Object.entries(manualPicks || {})) {
    if (!val) continue;
    const opt = (catalog.options || []).find((o) => o.option === key);
    const matched = matchAnprintOptionValue(val, opt?.values || []);
    if (matched) snappedManual[key] = matched;
  }
  picks = { ...picks, ...snappedManual };
  picks = applySingleValueOptions(catalog, picks);

  let variant = findAnprintVariant(catalog, picks);

  if (!variant) {
    const provided = keys.filter((k) => picks[k]);
    if (provided.length) {
      const candidates = (catalog.variants || []).filter((v) =>
        provided.every((k) => norm(v[k]) === norm(picks[k]))
      );
      if (candidates.length === 1) {
        variant = candidates[0];
        keys.forEach((k) => {
          if (variant[k]) picks[k] = variant[k];
        });
      }
    }
  }

  const sku = variant?.sku || null;

  return {
    orderSize,
    orderColor,
    picks,
    variant,
    matched: !!variant,
    catalogSku: catalog?.sku || null,
    sku,
    variant_id: sku,
  };
};

const fieldFromSize = (sizeStr, label) => {
  const m = String(sizeStr || '').match(new RegExp(`${label}:\\s*([^\\-\\n]+)`, 'i'));
  return (m?.[1] || '').trim();
};

export const parseAnprintOrderStyle = (orderItem) => {
  const sizeStr = orderItem?.size || '';
  return {
    type: fieldFromSize(sizeStr, 'Type'),
    brand: fieldFromSize(sizeStr, 'Fabric Brand'),
    style: fieldFromSize(sizeStr, 'Style'),
  };
};

/**
 * Suy catalog Anprint từ Type / Brand / Style trên đơn.
 * Unisex Gildan T-Shirt → 5000; Youth → 5000B; Comfort → 1717; Bella → 3001.
 * Women / style không có catalog → requireManual (không auto gán Unisex 5000).
 */
export const detectAnprintCatalogId = (orderItem) => {
  const { type, brand, style } = parseAnprintOrderStyle(orderItem);
  const hay = `${orderItem?.size || ''} ${orderItem?.items_name || ''} ${orderItem?.sku || ''} ${orderItem?.product || ''}`;
  const compact = hay.replace(/[\s_-]+/g, '').toUpperCase();
  const upper = hay.toUpperCase();
  const typeU = type.toUpperCase();
  const styleU = style.toUpperCase();
  const brandU = brand.toUpperCase();
  const isTee = /T-?SHIRT|TSHIRT|\bTEE\b/.test(styleU) || /TSHIRT/.test(compact) || /T-?SHIRT/.test(upper);
  const isWomen = /^(WOMEN|WOMAN|LADIES|LADY)$/.test(typeU) || /WOMEN/.test(styleU);
  const isYouth = /^YOUTH$/.test(typeU) || /YOUTH/.test(styleU);

  if (/CAMO/.test(compact) || /CAMO\s*TSHIRT/.test(upper)) {
    return { catalogId: 'CAMO', requireManual: false };
  }
  if (/HOODIE/.test(compact) && !isTee) {
    return { catalogId: '18500', requireManual: false };
  }
  if (/SWEATSHIRT/.test(compact)) {
    return { catalogId: '18000', requireManual: false };
  }
  if (isYouth && isTee) {
    return { catalogId: '5000B', requireManual: false };
  }
  if (isWomen && isTee) {
    return {
      catalogId: null,
      requireManual: true,
      reason: 'Anprint không có Women T-Shirt — chọn catalog/SKU thủ công',
    };
  }
  if (/V-?NECK/.test(styleU) || /TANK/.test(styleU) || /LONG\s*SLEEVE/.test(styleU)) {
    return {
      catalogId: null,
      requireManual: true,
      reason: 'Anprint chưa có catalog cho style này — chọn thủ công',
    };
  }
  if (/COMFORT/.test(brandU) || /COMFORTCOLOR/.test(compact)) {
    return { catalogId: '1717', requireManual: false };
  }
  if (/BELLA/.test(brandU) || /3001/.test(compact)) {
    return { catalogId: '3001', requireManual: false };
  }
  if (isTee) {
    return { catalogId: '5000', requireManual: false };
  }

  return {
    catalogId: null,
    requireManual: true,
    reason: 'Anprint chưa map được style — chọn catalog/SKU thủ công',
  };
};

export const flattenAnprintVariants = (catalogs = []) =>
  catalogs.flatMap((catalog) =>
    (catalog.variants || []).map((variant) => ({
      ...variant,
      catalogId: catalog.id,
      catalogName: catalog.name,
      label: `${variant.sku} · ${catalog.name} · ${variant.size || ''} / ${variant.color || ''}`.replace(/\s+\/\s+$/, ''),
    }))
  );

export const resolveAnprintFromVariant = (catalog, variant) => {
  const keys = getCatalogOptionKeys(catalog);
  const picks = {};
  keys.forEach((k) => {
    if (variant?.[k]) picks[k] = variant[k];
  });
  return {
    orderSize: variant?.size || null,
    orderColor: variant?.color || null,
    picks,
    variant,
    matched: !!variant?.sku,
    catalogSku: catalog?.sku || null,
    sku: variant?.sku || null,
    variant_id: variant?.sku || null,
  };
};

/**
 * Tự map catalog Anprint theo style trong size + size/color order.
 */
export const autoResolveAnprint = (catalogs, orderItem) => {
  const detected = detectAnprintCatalogId(orderItem);
  const preferredId = detected.catalogId || null;
  const list = catalogs || [];

  if (detected.requireManual) {
    return {
      matched: false,
      requireManual: true,
      reason: detected.reason,
      preferredId,
      matches: [],
      catalog: null,
      result: null,
    };
  }

  // Ưu tiên catalog mặc định theo style (Unisex T-Shirt → 5000, Hoodie, SweatShirt...)
  if (preferredId) {
    const preferred = list.find((c) => String(c.id) === String(preferredId));
    if (preferred) {
      const result = resolveAnprint(preferred, orderItem, {});
      if (result.matched && result.sku) {
        return {
          matched: true,
          preferredId,
          matches: [{ catalog: preferred, result }],
          catalog: preferred,
          result,
        };
      }
      // Style đã rõ nhưng chưa match size/color → vẫn preselect catalog
      return {
        matched: false,
        preferredId,
        preselectCatalog: preferred,
        matches: [],
        catalog: preferred,
        result,
      };
    }
  }

  const matches = [];
  for (const catalog of list) {
    const result = resolveAnprint(catalog, orderItem, {});
    if (result.matched && result.sku) {
      matches.push({ catalog, result });
    }
  }

  if (!matches.length) {
    return {
      matched: false,
      requireManual: true,
      reason: 'Anprint chưa map được variant — chọn catalog/SKU thủ công',
      preferredId,
      matches: [],
      result: null,
      catalog: null,
    };
  }

  if (matches.length === 1) {
    return {
      matched: true,
      preferredId,
      matches,
      catalog: matches[0].catalog,
      result: matches[0].result,
    };
  }

  return {
    matched: false,
    ambiguous: true,
    requireManual: true,
    reason: 'Anprint: nhiều catalog cùng size/color — vui lòng chọn catalog/SKU',
    preferredId,
    matches,
    catalog: null,
    result: null,
  };
};
